import get from 'lodash/get'
import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import Dropdown, { OptionType } from '../Dropdown'
import { RendererOptions } from '../DataTable'
import { Typography } from '@material-ui/core'
import { startCase } from 'lodash'
import ContainerSizeIcon from '../../assets/public/v2/size.svg'
import { Icon } from './RendererHoc'
import { RendererRoot } from './RendererHoc'

const StyledText = styled(Typography)`
  font-size: inherit;
  color: ${(props) => props.theme.palette.text.primary};

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

function ContainerSizeRenderer<T extends Record<string, any>>({
  anchorEl: outerAnchorEl,
  identifier,
  name: columnName,
  rowData,
  value: externalValue,
  icon,
  onSubmit,
  editable,
  sizeOptions,
}: RendererOptions<T>) {
  const optionValues = sizeOptions || get(rowData, identifier)
  const data = Array.isArray(optionValues) ? optionValues : [optionValues]

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(outerAnchorEl)
  const [search, setSearch] = React.useState((externalValue as string) || '')
  const open = Boolean(anchorEl)

  const options = useMemo(
    () =>
      data.map((en: any) => ({
        label: en.title,
        value: en.value,
        icon: ContainerSizeIcon,
      })),
    [data]
  )

  const rendererValue = get(rowData, identifier)
  const optionValueOption: OptionType = useMemo(
    () =>
      options.find((opt: OptionType) => opt.value === rendererValue) || {
        label: startCase(rendererValue),
        value: rendererValue,
        icon: icon,
      },
    [rendererValue]
  )

  const [_value, setValue] = useState<OptionType>(optionValueOption)
  const value = options.find((opt: OptionType) => opt.value === externalValue) || _value

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    editable && setAnchorEl(event.currentTarget)
  }

  const handleOnChange = (value: OptionType) => {
    setValue(value)
    onSubmit?.({ slug: rowData.slug, [identifier]: value.value } as any)
    handleClose()
  }

  return (
    <>
      <RendererRoot
        role='button'
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        data-disabled={rowData.isArchived}
      >
        <Icon src={ContainerSizeIcon} />
        <StyledText title={value?.label}>{value?.label || '--'}</StyledText>
      </RendererRoot>
      <Dropdown
        title={columnName}
        search={search}
        onSearch={(value) => setSearch(value)}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        options={options}
        onSelect={handleOnChange}
        value={value?.value}
      />
    </>
  )
}

ContainerSizeRenderer.operators = ['eq', 'neq', 'is_null', 'not_null']

export default ContainerSizeRenderer
